import React, { useState, useCallback, useRef, useMemo } from 'react';
import { BookOpenIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContentLarge } from '@/components/ui/dialog';
import { HANDBOOK_CHAPTERS } from './handbookChapters';
import type { HandbookChapter } from './handbookChapters';

interface PlaybookHandbookProps {
  isOpen: boolean;
  onClose: () => void;
  initialChapter?: string;
}

const PlaybookHandbook: React.FC<PlaybookHandbookProps> = ({ isOpen, onClose, initialChapter }) => {
  const [activeChapterId, setActiveChapterId] = useState(
    initialChapter && HANDBOOK_CHAPTERS.some((c) => c.id === initialChapter)
      ? initialChapter
      : 'what-is-playbook',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const filteredChapters = useMemo(() => {
    if (!searchQuery.trim()) return HANDBOOK_CHAPTERS;
    const q = searchQuery.toLowerCase();
    return HANDBOOK_CHAPTERS.filter(
      (ch) =>
        ch.title.toLowerCase().includes(q) ||
        ch.subtitle.toLowerCase().includes(q) ||
        ch.keywords.some((k) => k.includes(q)),
    );
  }, [searchQuery]);

  const activeChapter = useMemo(
    () => HANDBOOK_CHAPTERS.find((c) => c.id === activeChapterId) ?? HANDBOOK_CHAPTERS[0],
    [activeChapterId],
  );

  const handleSelectChapter = useCallback((ch: HandbookChapter) => {
    setActiveChapterId(ch.id);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setSearchQuery(val);
      // If search narrows to one result, auto-select it
      if (val.trim()) {
        const q = val.toLowerCase();
        const matches = HANDBOOK_CHAPTERS.filter(
          (ch) =>
            ch.title.toLowerCase().includes(q) ||
            ch.subtitle.toLowerCase().includes(q) ||
            ch.keywords.some((k) => k.includes(q)),
        );
        if (matches.length === 1) {
          setActiveChapterId(matches[0].id);
          contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // If active chapter is filtered out, select first visible
        if (matches.length > 0 && !matches.some((m) => m.id === activeChapterId)) {
          setActiveChapterId(matches[0].id);
          contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    [activeChapterId],
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContentLarge className="bg-[var(--brand-surface-card)] border border-[var(--brand-border)] shadow-2xl overflow-hidden">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--brand-border)]">
            <div className="flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-lex-accent" />
              <h1 className="text-lg font-semibold text-[var(--brand-text-primary)]">EdgePlaybook Handbook</h1>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)] transition-colors"
              aria-label="Close handbook"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body: sidebar + content */}
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Left sidebar */}
            <aside className="w-56 border-r border-[var(--brand-border)] bg-[var(--brand-surface-sidebar)] flex flex-col">
              {/* Search */}
              <div className="p-3 border-b border-[var(--brand-border)]">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--brand-text-tertiary)]" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search chapters..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-[var(--brand-border)] bg-[var(--brand-surface-card)] text-[var(--brand-text-primary)] placeholder:text-[var(--brand-text-tertiary)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-focus-ring)]"
                  />
                </div>
              </div>

              {/* Chapter list */}
              <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {filteredChapters.map((ch) => {
                  const isActive = ch.id === activeChapterId;
                  const Icon = ch.icon;
                  return (
                    <button
                      key={ch.id}
                      onClick={() => handleSelectChapter(ch)}
                      className={`flex items-start w-full px-2.5 py-2 rounded-lg text-left transition-colors ${
                        isActive
                          ? 'bg-[var(--brand-accent-primary-soft)] text-[var(--brand-accent-primary)]'
                          : 'text-[var(--brand-text-secondary)] hover:bg-[var(--brand-surface-hover)]'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-medium truncate">{ch.title}</div>
                        <div className={`text-[10px] truncate ${isActive ? 'opacity-70' : 'text-[var(--brand-text-tertiary)]'}`}>
                          {ch.subtitle}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {filteredChapters.length === 0 && (
                  <p className="text-xs text-[var(--brand-text-tertiary)] text-center py-4">No chapters match your search.</p>
                )}
              </nav>
            </aside>

            {/* Right content area */}
            <section ref={contentRef} className="flex-1 overflow-y-auto">
              {activeChapter.render()}
            </section>
          </div>
        </div>
      </DialogContentLarge>
    </Dialog>
  );
};

export default PlaybookHandbook;
